import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  ComplianceReview,
  ComplianceReviewRequirement,
  ComplianceFinding,
  ComplianceAction,
  ComplianceReviewStatusHistory,
  ComplianceVerdict
} from './types';
import { monitoringService } from './monitoringService';

interface Props {
  review: ComplianceReview;
  onClose: () => void;
  onRefresh: () => void;
}

export const ComplianceReviewDetailModal: React.FC<Props> = ({ review, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'REQUIREMENTS' | 'FINDINGS' | 'ACTIONS' | 'HISTORY'>('OVERVIEW');
  const [requirements, setRequirements] = useState<ComplianceReviewRequirement[]>([]);
  const [findings, setFindings] = useState<ComplianceFinding[]>([]);
  const [actions, setActions] = useState<ComplianceAction[]>([]);
  const [history, setHistory] = useState<ComplianceReviewStatusHistory[]>([]);
  const [loading, setLoading] = useState(false);

  // Requirement evaluation modal form
  const [showAddReq, setShowAddReq] = useState(false);
  const [selectedReqId, setSelectedReqId] = useState('CMP-REQ-001');
  const [selectedReqCode, setSelectedReqCode] = useState('CMP-ORO-GEN-200-1');
  const [selectedReqTitle, setSelectedReqTitle] = useState('Accountable Executive Safety Responsibility');

  const [showAssessmentForm, setShowAssessmentForm] = useState<string | null>(null);
  const [asmtResult, setAsmtResult] = useState<ComplianceVerdict>('COMPLIANT');
  const [asmtJustification, setAsmtJustification] = useState('');
  const [asmtGap, setAsmtGap] = useState('');

  // Finding form
  const [showAddFinding, setShowAddFinding] = useState(false);
  const [findingCode, setFindingCode] = useState(`FND-CMP-${Date.now().toString(36).toUpperCase()}`);
  const [findingTitle, setFindingTitle] = useState('');
  const [findingDesc, setFindingDesc] = useState('');
  const [findingDueDate, setFindingDueDate] = useState(new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));

  const loadTabData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'REQUIREMENTS') {
        const data = await monitoringService.getReviewRequirements(review.id);
        setRequirements(data);
      } else if (activeTab === 'FINDINGS') {
        const data = await monitoringService.getFindings(review.id);
        setFindings(data);
      } else if (activeTab === 'ACTIONS') {
        const data = await monitoringService.getActions(undefined);
        setActions(data.filter(a => a.review_id === review.id));
      } else if (activeTab === 'HISTORY') {
        const data = await monitoringService.getReviewStatusHistory(review.id);
        setHistory(data);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTabData();
  }, [activeTab, review.id]);

  const handleTransition = async (newStatus: string) => {
    const reason = prompt(`Please specify a justification for transitioning status to '${newStatus}':`);
    if (!reason) return;
    try {
      await monitoringService.transitionReview(review.id, newStatus, reason, 'Lead Compliance Auditor');
      onRefresh();
      onClose();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddReqSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await monitoringService.addReviewRequirement(review.id, {
        compliance_requirement_id: selectedReqId,
        req_code: selectedReqCode,
        req_title: selectedReqTitle
      });
      setShowAddReq(false);
      loadTabData();
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAssessmentSubmit = async (complianceReqId: string) => {
    try {
      await monitoringService.recordAssessment(review.id, {
        compliance_requirement_id: complianceReqId,
        assessor_id: 'USR-COMPL-MGR',
        assessor_name: 'Lead Compliance Auditor',
        result: asmtResult,
        justification: asmtJustification,
        gap_summary: asmtGap || undefined
      });
      setShowAssessmentForm(null);
      setAsmtJustification('');
      setAsmtGap('');
      loadTabData();
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddFindingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await monitoringService.createFinding({
        finding_code: findingCode,
        review_id: review.id,
        title: findingTitle,
        description: findingDesc,
        target_due_date: findingDueDate,
        created_by: 'Lead Compliance Auditor'
      });
      setShowAddFinding(false);
      setFindingTitle('');
      setFindingDesc('');
      loadTabData();
      onRefresh();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header">
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                {review.review_number}
              </span>
              <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-800 font-semibold">
                {review.status}
              </span>
              {review.compliance_verdict && (
                <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">
                  VERDICT: {review.compliance_verdict}
                </span>
              )}
            </div>
            <h3 style={{ fontSize: '19px', fontWeight: 700, margin: 0, color: '#0f172a' }}>{review.title}</h3>
          </div>
          <div className="flex items-center gap-2">
            {review.status === 'DRAFT' && (
              <button
                type="button"
                onClick={() => handleTransition('IN_PROGRESS')}
                className="btn-primary"
              >
                Start Review
              </button>
            )}
            {review.status === 'IN_PROGRESS' && (
              <button
                type="button"
                onClick={() => handleTransition('COMPLETED')}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold shadow-sm transition"
              >
                Complete Review
              </button>
            )}
            <button type="button" onClick={onClose} className="modal-close-btn" title="Kapat" aria-label="Kapat">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 bg-gray-50 px-6 gap-2 text-xs font-medium">
          {(['OVERVIEW', 'REQUIREMENTS', 'FINDINGS', 'ACTIONS', 'HISTORY'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-3 border-b-2 transition ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-700 font-bold'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'OVERVIEW' && 'Overview'}
              {tab === 'REQUIREMENTS' && `In-Scope Requirements (${requirements.length || review.requirements_count || 0})`}
              {tab === 'FINDINGS' && `Findings (${review.findings_count})`}
              {tab === 'ACTIONS' && `Actions (${review.actions_count})`}
              {tab === 'HISTORY' && 'Audit History'}
            </button>
          ))}
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {activeTab === 'OVERVIEW' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200 text-xs">
                <div>
                  <span className="text-gray-500 block">Target Department</span>
                  <span className="font-semibold text-gray-900 text-sm">{review.target_department}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Lead Reviewer</span>
                  <span className="font-semibold text-gray-900 text-sm">{review.lead_reviewer_name}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Review Type & Methodology</span>
                  <span className="font-semibold text-gray-900 text-sm">{review.review_type} — {review.review_method}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Independence Verification</span>
                  <span className="font-semibold text-emerald-800 text-sm">
                    {review.independence_verified ? '✓ Verified (Independent QA Function)' : 'Unverified'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">Scheduled Period</span>
                  <span className="font-semibold text-gray-900 font-mono">{review.scheduled_start} - {review.scheduled_end}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Actual Execution</span>
                  <span className="font-semibold text-gray-900 font-mono">{review.actual_start || 'Not yet started'} - {review.actual_end || ''}</span>
                </div>
              </div>

              {review.executive_summary && (
                <div className="p-4 bg-white rounded-xl border border-gray-200">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">Executive Summary</h4>
                  <p className="text-xs text-gray-800 leading-relaxed">
                    {review.executive_summary}
                  </p>
                </div>
              )}

              {review.independence_notes && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-900">
                  <strong>Independence Note:</strong> {review.independence_notes}
                </div>
              )}
            </div>
          )}

          {activeTab === 'REQUIREMENTS' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-xs text-gray-500">Regulatory standards and compliance obligations audited under this review</p>
                <button
                  type="button"
                  onClick={() => setShowAddReq(!showAddReq)}
                  className="btn-primary"
                >
                  {showAddReq ? 'Cancel' : '+ Add In-Scope Requirement'}
                </button>
              </div>

              {showAddReq && (
                <form onSubmit={handleAddReqSubmit} className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                  <div className="form-field">
                    <label>Select Regulatory Requirement</label>
                    <select
                      value={selectedReqId}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedReqId(val);
                        if (val === 'CMP-REQ-001') {
                          setSelectedReqCode('CMP-ORO-GEN-200-1');
                          setSelectedReqTitle('Accountable Executive Safety Responsibility');
                        } else if (val === 'CMP-REQ-002') {
                          setSelectedReqCode('CMP-ORO-GEN-200-3');
                          setSelectedReqTitle('Proactive Safety Risk Management');
                        } else if (val === 'CMP-REQ-003') {
                          setSelectedReqCode('CMP-CAT-OP-MPA-100');
                          setSelectedReqTitle('Automated Flight Dispatch & Fuel Check Verification');
                        } else {
                          setSelectedReqCode('CMP-SHT-OPS-035');
                          setSelectedReqTitle('National Quality Management System Audit Program');
                        }
                      }}
                    >
                      <option value="CMP-REQ-001">CMP-ORO-GEN-200-1: Accountable Executive Safety Responsibility</option>
                      <option value="CMP-REQ-002">CMP-ORO-GEN-200-3: Proactive Safety Risk Management</option>
                      <option value="CMP-REQ-003">CMP-CAT-OP-MPA-100: Automated Flight Dispatch & Fuel Check Verification</option>
                      <option value="CMP-REQ-004">CMP-SHT-OPS-035: National Quality Management System Audit Program</option>
                    </select>
                  </div>
                  <div className="flex justify-end pt-2">
                    <button type="submit" className="btn-primary">
                      Add to Review Scope
                    </button>
                  </div>
                </form>
              )}

              {loading ? (
                <div className="text-center py-6 text-xs text-gray-400">Loading requirements...</div>
              ) : requirements.length === 0 ? (
                <div className="text-center py-6 text-xs text-gray-400">No requirements added to review scope.</div>
              ) : (
                <div className="space-y-3">
                  {requirements.map((req) => (
                    <div key={req.id} className="p-4 bg-white border border-gray-200 rounded-xl text-xs space-y-2">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-bold text-gray-900 font-mono">{req.req_code}</span> — <span className="font-medium text-gray-800">{req.req_title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 font-semibold text-[11px]">
                            {req.assessment_status}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowAssessmentForm(showAssessmentForm === req.id ? null : req.id)}
                            className="text-blue-600 hover:text-blue-800 font-semibold text-xs ml-2"
                          >
                            Evaluate
                          </button>
                        </div>
                      </div>

                      {showAssessmentForm === req.id && (
                        <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-200 space-y-3 mt-2">
                          <div className="form-grid-2">
                            <div className="form-field">
                              <label>Evaluation Outcome</label>
                              <select
                                value={asmtResult}
                                onChange={(e) => setAsmtResult(e.target.value as ComplianceVerdict)}
                              >
                                <option value="COMPLIANT">Compliant (COMPLIANT)</option>
                                <option value="PARTIALLY_COMPLIANT">Partially Compliant (PARTIALLY COMPLIANT)</option>
                                <option value="NON_COMPLIANT">Non-Compliant (NON-COMPLIANT)</option>
                                <option value="INSUFFICIENT_EVIDENCE">Insufficient Evidence (INSUFFICIENT EVIDENCE)</option>
                              </select>
                            </div>
                            <div className="form-field">
                              <label>Compliance Justification *</label>
                              <input
                                type="text"
                                placeholder="Audit evidence reference, operational observations..."
                                value={asmtJustification}
                                onChange={(e) => setAsmtJustification(e.target.value)}
                                required
                              />
                            </div>
                          </div>
                          <div className="flex justify-end pt-1">
                            <button
                              type="button"
                              onClick={() => handleAssessmentSubmit(req.compliance_requirement_id)}
                              className="btn-primary"
                            >
                              Save Evaluation
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'FINDINGS' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-xs text-gray-500">Non-compliances and deficiencies recorded during inspection</p>
                <button
                  type="button"
                  onClick={() => setShowAddFinding(!showAddFinding)}
                  className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-semibold shadow-sm transition"
                >
                  {showAddFinding ? 'Cancel' : '+ Log Finding'}
                </button>
              </div>

              {showAddFinding && (
                <form onSubmit={handleAddFindingSubmit} className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                  <div className="form-grid-2">
                    <div className="form-field">
                      <label>Finding Code *</label>
                      <input
                        type="text"
                        value={findingCode}
                        onChange={(e) => setFindingCode(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-field">
                      <label>Target Resolution Date *</label>
                      <input
                        type="date"
                        value={findingDueDate}
                        onChange={(e) => setFindingDueDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Finding Title *</label>
                    <input
                      type="text"
                      placeholder="Title of regulatory non-compliance..."
                      value={findingTitle}
                      onChange={(e) => setFindingTitle(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Finding Description & Audit Observation *</label>
                    <textarea
                      value={findingDesc}
                      onChange={(e) => setFindingDesc(e.target.value)}
                      rows={2}
                      placeholder="Specific condition, criteria, and evidence observed..."
                      required
                    />
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-semibold shadow-sm transition"
                    >
                      Save Finding
                    </button>
                  </div>
                </form>
              )}

              {loading ? (
                <div className="text-center py-6 text-xs text-gray-400">Loading findings...</div>
              ) : findings.length === 0 ? (
                <div className="text-center py-6 text-xs text-gray-400">No findings recorded.</div>
              ) : (
                <div className="space-y-2">
                  {findings.map((f) => (
                    <div key={f.id} className="p-4 bg-white border border-gray-200 rounded-xl text-xs space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-900 font-mono">{f.finding_code}: {f.title}</span>
                        <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 font-semibold text-[10px]">{f.severity}</span>
                      </div>
                      <div className="text-gray-600">{f.description}</div>
                      <div className="text-gray-400 text-[11px] pt-1">Due Date: {f.target_due_date} • Status: {f.status}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'ACTIONS' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">Corrective actions linked to inspection or audit findings</p>
              {loading ? (
                <div className="text-center py-6 text-xs text-gray-400">Loading actions...</div>
              ) : actions.length === 0 ? (
                <div className="text-center py-6 text-xs text-gray-400">No linked corrective actions on file.</div>
              ) : (
                <div className="space-y-2">
                  {actions.map((a) => (
                    <div key={a.id} className="p-4 bg-white border border-gray-200 rounded-xl text-xs space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-900 font-mono">{a.action_code}: {a.title}</span>
                        <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold text-[10px]">{a.status}</span>
                      </div>
                      <div className="text-gray-600">{a.description}</div>
                      <div className="text-gray-400 text-[11px] pt-1">
                        Assignee: {a.assignee_name} ({a.assigned_department}) • Due: {a.internal_due_date}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'HISTORY' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500">Inspection status transitions and audit trail</p>
              {loading ? (
                <div className="text-center py-6 text-xs text-gray-400">Loading history...</div>
              ) : history.length === 0 ? (
                <div className="text-center py-6 text-xs text-gray-400">No history records logged.</div>
              ) : (
                <div className="space-y-2">
                  {history.map((h) => (
                    <div key={h.id} className="p-3 bg-white border border-gray-200 rounded-xl text-xs space-y-1">
                      <div className="flex justify-between text-gray-600">
                        <span className="font-bold text-blue-700">{h.from_status} → {h.to_status}</span>
                        <span className="text-[11px] font-mono text-gray-400">{h.created_at}</span>
                      </div>
                      <div className="text-gray-800 font-medium">{h.reason}</div>
                      <div className="text-gray-400 text-[11px] font-mono">Changed By: {h.changed_by}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
